import { Component, EventEmitter, Output } from '@angular/core';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { BasketRepository } from '../../../../shared/repositories/basket.repository';
import { Observable, shareReplay, tap } from 'rxjs';
import { BasketItem } from '../../../../shared/interfaces/basket-item.interface';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { StatusState } from '@ngneat/elf-requests';
import { CounterComponent } from '../../../../shared/components/counter/counter.component';
import { CurrencyComponent } from '../../../../shared/components/price/currency.component';
import { PictureFallbackDirective } from '../../../../shared/directives/picture-fallback/picture-fallback.directive';
import { UIMenuItemDto } from '../../../../shared/interfaces/ui-menu-item-dto.interface';
import { RelatedCardComponent } from '../../../../components/related-card/related-card.component';
import { DragTranslateDirective } from '../../../../shared/directives/drag-translate/drag-translate.directive';
import { ConfigRepository } from '../../../../shared/repositories/config.repository';
import { SessionRepository } from 'src/app/shared/repositories/session.repository';
import { CostRule } from '../../../../api/v1/models/cost-rule';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AddressDto } from '../../../../api/v1/models/address-dto';
import { OrderDeliveryModalService } from '../../../../components/order-delivery-modal/order-delivery-modal.service';
import { MinimalPriceMessageComponent } from '../order-form/minimal-price-message/minimal-price-message.component';

interface IForm {
  counters: FormArray;
}

@Component({
  selector: 'app-basket-mini',
  standalone: true,
  templateUrl: './basket-mini.component.html',
  styleUrls: ['./basket-mini.component.scss'],
  imports: [
    NgIf,
    NgFor,
    AsyncPipe,
    ReactiveFormsModule,
    CounterComponent,
    CurrencyComponent,
    PictureFallbackDirective,
    RelatedCardComponent,
    DragTranslateDirective,
    MatProgressSpinnerModule,
    MinimalPriceMessageComponent,
  ]
})
export class BasketMiniComponent {
  constructor(
    private sessionRepository: SessionRepository,
    private basketRepository: BasketRepository,
    private configRepository: ConfigRepository,
    private orderDeliveryModal: OrderDeliveryModalService,
  ) { }

  @Output() emptyBasket: EventEmitter<unknown> = new EventEmitter<unknown>();

  deliveryAddress$: Observable<AddressDto | null> = this.sessionRepository.deliveryAddress$;
  isDelivery$: Observable<boolean> = this.sessionRepository.isDelivery$;
  actualCostRule$: Observable<CostRule | null> = this.sessionRepository.actualCostRule$;

  nextDeliveryLimit$: Observable<number | null> = this.sessionRepository.nextDeliveryLimit$;
  needToAdd$: Observable<number> = this.sessionRepository.needToAdd$;

  isBasketProcessing$: Observable<StatusState> = this.sessionRepository.isBasketProcessing$.pipe(
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  infinity = Infinity;

  form: FormGroup<IForm> = new FormGroup<IForm>({
    counters: new FormArray<FormControl<{ count: number }[]>>([])
  });

  relatedProducts$: Observable<UIMenuItemDto[]> = this.sessionRepository.basketRelatedItems$;

  guestNumberControl = new FormControl<number>(this.sessionRepository.guestNumberCount);

  isEatinMode$: Observable<boolean> = this.configRepository.isEatinMode$;

  data$: Observable<BasketItem[]> = this.sessionRepository.basketItems$.pipe(
    tap(data => {
      if (!data) { return }
      this.prepareCounterArray(data.length);
      this.form.controls.counters.patchValue(data);
    })
  );

  private prepareCounterArray(length: number): void {
    this.form.controls.counters.clear()
    for (let i = 0; i < length; i++) {
      this.form.controls.counters.push(
        new FormGroup({ count: new FormControl(1) })
      );
    }
  }

  onChangeCount(itemKey: string, count: number): void {
    if (count == 0) {
      this.basketRepository.removeItem(itemKey)
    }
    else {
      this.basketRepository.updateCount(itemKey, count);
    }

    if (this.form.controls.counters.length === 0) {
      this.emptyBasket.emit();
    }
  }

  onChangeGuestCount(count: number) {
    this.sessionRepository.setNumberOfGuest(count)
  }

  onChooseAddress() {
    this.orderDeliveryModal.setOrderTypeModalState(true);
  }
}
